import React, { useState, useEffect, useRef } from 'react';
import {
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Box,
  TextField,
  useMediaQuery,
  Card,
  CardContent,
  Stack,
  Chip,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Search as SearchIcon,
  SwapHoriz as SwapHorizIcon,
} from '@mui/icons-material';
import PrintIcon from '@mui/icons-material/Print';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import BarcodeIcon from '../../components/inventario/BarcodeIcon';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import api from '../../services/api';
import PDFViewer from '../../components/shared/PDFViewer';
import ImprimirEtiquetasCodigosDialog, {
  EtiquetaDocumentoDetalle,
} from '../../components/compras/ImprimirEtiquetasCodigosDialog';
import { getFileNameFromContentDisposition } from '../../utils/contentDisposition';
import ajusteInventarioService, {
  detallesEtiquetaDesdeAjuste,
} from '../../services/ajusteInventarioService';
import { IFormularioAjuste, IItemAjuste } from '../../interfaces/AjusteInventario';
import { useTheme } from '@mui/material/styles';
import SearchableSelect from '../../components/SearchableSelect';
import FiltroSucursalVentaSelect from '../../components/shared/FiltroSucursalVentaSelect';
import { resolveNroSucFiltro, useSucursalesVentaFiltro } from '../../hooks/useSucursalesVentaFiltro';
import Can from '../../components/Can';
import { PERMISO_AJUSTE_INVENTARIO } from '../../config/permisosCatalogoHelper';
import { getAccessToken } from '../../utils/sessionAuthStore';
import { exportAjusteInventarioExcel } from '../../utils/ajusteInventarioExcelExport';

const textoOrigenMovimiento = (tipo: string): string =>
  tipo.toLowerCase().includes('transferencia') ? 'la transferencia' : 'el ajuste';

const TIPO_MOVIMIENTO_AJUSTE_OPTIONS = [
  { value: 'todos', label: 'Todos' },
  { value: 'positivo', label: 'Ajuste Positivo' },
  { value: 'negativo', label: 'Ajuste Negativo' },
  { value: 'transferencia', label: 'Transferencia' },
] as const;

const getFirstDayOfMonth = (): string => {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().split('T')[0];
};

const getLastDayOfMonth = (): string => {
  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  d.setDate(0);
  return d.toISOString().split('T')[0];
};

const Transferencias: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [ajustes, setAjustes] = useState<IFormularioAjuste[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [fechaDesde, setFechaDesde] = useState<string>(getFirstDayOfMonth);
  const [fechaHasta, setFechaHasta] = useState<string>(getLastDayOfMonth);
  const [tipoMovimiento, setTipoMovimiento] = useState<string>('todos');
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [selectedAjusteNro, setSelectedAjusteNro] = useState<number | null>(null);
  const [pdfFileName, setPdfFileName] = useState<string | null>(null);
  const [exportingExcelNro, setExportingExcelNro] = useState<number | null>(null);
  const [etiquetasOpen, setEtiquetasOpen] = useState(false);
  const [etiquetasCtx, setEtiquetasCtx] = useState<{
    nroFormulario: number;
    detalles: EtiquetaDocumentoDetalle[];
    textoOrigen: string;
  } | null>(null);
  const [pdfEtiquetas, setPdfEtiquetas] = useState<{ url: string; fileName: string } | null>(null);
  const etiquetasPedidasRef = useRef(false);
  const pdfEtiquetasRef = useRef<{ url: string; fileName: string } | null>(null);
  const { filtroNroSuc, setFiltroNroSuc, sucursales, loadingSucursales, filtroListo } = useSucursalesVentaFiltro();

  useEffect(() => {
    pdfEtiquetasRef.current = pdfEtiquetas;
  }, [pdfEtiquetas]);

  useEffect(() => {
    return () => {
      const prev = pdfEtiquetasRef.current;
      if (prev?.url) URL.revokeObjectURL(prev.url);
    };
  }, []);

  // Efecto separado para manejar el refresh desde el formulario
  useEffect(() => {
    if (location.state?.refresh) {
      const nroFormularioGuardado = location.state?.nroFormularioGuardado;
      const tipoAjusteGuardado = location.state?.tipoAjusteGuardado;
      console.log('🔄 Refresh detectado desde formulario, reseteando filtros y forzando recarga...');
      console.log('📋 Nro. Formulario guardado:', nroFormularioGuardado);
      console.log('📋 Tipo de ajuste guardado:', tipoAjusteGuardado);
      console.log('📋 Estado actual de ajustes antes de resetear filtros:', ajustes.length);
      
      // IMPORTANTE: NO resetear los ajustes del estado antes de cargar
      // Esto permite que los ajustes preservados se mantengan
      
      // Resetear a la primera página cuando se viene desde el formulario
      setPage(0);
      setSearchTerm(''); // Limpiar búsqueda para mostrar todos
      setFechaDesde(getFirstDayOfMonth());
      setFechaHasta(getLastDayOfMonth());
      setTipoMovimiento('todos'); // Mostrar todos los tipos por defecto
      setFiltroNroSuc(null);
      // IMPORTANTE: Mantener el nroFormularioGuardado en el state para que se pueda buscar después
      // No limpiar completamente el state, solo el refresh
      const pedirEtiquetas = !!location.state?.pedirEtiquetas;
      navigate(location.pathname, { replace: true, state: { nroFormularioGuardado, tipoAjusteGuardado, pedirEtiquetas } });
      
      // IMPORTANTE: NO llamar a loadAjustes aquí porque el useEffect principal ya lo hará
      // El useEffect principal se ejecutará cuando cambien page, searchTerm, etc.
      // Esto asegura que los ajustes del estado anterior se preserven correctamente
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.refresh]);

  // Efecto principal para cargar ajustes
  // IMPORTANTE: Este efecto se ejecuta cuando cambian page, rowsPerPage, searchTerm, o location
  // La lista se actualiza automáticamente mediante GET /api/ajustes
  useEffect(() => {
    if (!filtroListo) return;
    console.log('🔄 useEffect principal - Cargando ajustes:', { page, rowsPerPage, searchTerm, pathname: location.pathname, key: location.key });
    loadAjustes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, searchTerm, fechaDesde, fechaHasta, filtroNroSuc, filtroListo, location.pathname, location.key]);

  // Helper function para verificar si una ubicación está vacía
  const isEmptyUbic = (v?: number | null) => {
    // Considerar vacío si es null, undefined, 0, o cualquier valor falsy
    return v === null || v === undefined || v === 0 || !v;
  };

  const loadAjustes = async () => {
    try {
      setLoading(true);
      console.log('🔄 Cargando ajustes del backend mediante GET /api/ajustes...', { page: page + 1, pageSize: rowsPerPage, searchTerm });
      
      // IMPORTANTE: Obtener la lista de ajustes mediante GET /api/ajustes
      // La lista se actualiza automáticamente cuando se hacen cambios o se busca un ajuste específico
      const nroSucFiltro = resolveNroSucFiltro(filtroNroSuc);
      const response = await ajusteInventarioService.getAjustes({
        pageNumber: page + 1,
        pageSize: rowsPerPage,
        searchTerm: searchTerm.trim() || undefined,
        ...(fechaDesde ? { fechaDesde } : {}),
        ...(fechaHasta ? { fechaHasta } : {}),
        ...(nroSucFiltro != null ? { nroSuc: nroSucFiltro } : {}),
      });
      
      console.log('📥 Respuesta de GET /api/ajustes:', {
        items: response.items?.length || 0,
        totalCount: response.totalCount,
        pageNumber: page + 1,
        pageSize: rowsPerPage
      });
      
      console.log('📥 Respuesta del backend:', {
        items: response.items?.length || 0,
        totalCount: response.totalCount,
        itemsRaw: response.items?.map((a: IFormularioAjuste) => ({
          nroFormulario: a.nroFormulario,
          nroUbicacionOrigen: a.nroUbicacionOrigen,
          nroUbicacionDestino: a.nroUbicacionDestino,
          tipoCliente: a.tipoCliente,
          nroTipoFormulario: a.nroTipoFormulario
        }))
      });
      
      // Normalizar las ubicaciones para asegurar que los ajustes negativos se identifiquen correctamente
      // El backend puede retornar 0, null, o undefined, todos deben ser tratados como vacío
      // IMPORTANTE: Convertir 0 a null para que isEmptyUbic funcione correctamente
      const ajustesNormalizados = response.items.map((a: IFormularioAjuste) => {
        const nroUbicacionOrigenNormalizado =
          a.nroUbicacionOrigen === 0 || a.nroUbicacionOrigen === null || a.nroUbicacionOrigen === undefined
            ? null
            : a.nroUbicacionOrigen;
        const nroUbicacionDestinoNormalizado =
          a.nroUbicacionDestino === 0 || a.nroUbicacionDestino === null || a.nroUbicacionDestino === undefined
            ? null
            : a.nroUbicacionDestino;

        // Normalizar también los datos de comprobante.
        // En algunos endpoints pueden venir como Letra / NroSuc / NroComprobante (PascalCase)
        // y en otros como letra / nroSuc / nroComprobante (camelCase).
        const anyAjuste = a as any;
        const letraNormalizada = a.letra ?? anyAjuste.Letra ?? null;
        const nroSucNormalizado = a.nroSuc ?? anyAjuste.NroSuc ?? null;
        const nroComprobanteNormalizado = a.nroComprobante ?? anyAjuste.NroComprobante ?? null;

        const ajusteNormalizado: IFormularioAjuste = {
          ...a,
          nroUbicacionOrigen: nroUbicacionOrigenNormalizado,
          nroUbicacionDestino: nroUbicacionDestinoNormalizado,
          letra: letraNormalizada,
          nroSuc: nroSucNormalizado,
          nroComprobante: nroComprobanteNormalizado,
        };
        
        // Verificar si es un ajuste negativo después de la normalización
        const esNegativo = !isEmptyUbic(ajusteNormalizado.nroUbicacionOrigen) && isEmptyUbic(ajusteNormalizado.nroUbicacionDestino);
        if (esNegativo) {
          console.log('🔍 Ajuste negativo detectado después de normalización:', {
            nroFormulario: ajusteNormalizado.nroFormulario,
            nroUbicacionOrigen: ajusteNormalizado.nroUbicacionOrigen,
            nroUbicacionDestino: ajusteNormalizado.nroUbicacionDestino,
            nroUbicacionOrigenOriginal: a.nroUbicacionOrigen,
            nroUbicacionDestinoOriginal: a.nroUbicacionDestino
          });
        }
        
        return ajusteNormalizado;
      });
      
      // Ordenar por nroFormulario descendente para mostrar los más recientes primero
      const ajustesOrdenados = [...ajustesNormalizados].sort((a, b) => {
        return (b.nroFormulario || 0) - (a.nroFormulario || 0);
      });
      
      console.log('📋 Ajustes cargados del backend:', ajustesOrdenados.map((a: IFormularioAjuste) => ({
        nroFormulario: a.nroFormulario,
        nroUbicacionOrigen: a.nroUbicacionOrigen,
        nroUbicacionDestino: a.nroUbicacionDestino,
        tipo: a.nroTipoFormulario,
        tipoCliente: a.tipoCliente,
        esNegativo: !isEmptyUbic(a.nroUbicacionOrigen) && isEmptyUbic(a.nroUbicacionDestino),
        esPositivo: isEmptyUbic(a.nroUbicacionOrigen) && !isEmptyUbic(a.nroUbicacionDestino),
        esTransferencia: !isEmptyUbic(a.nroUbicacionOrigen) && !isEmptyUbic(a.nroUbicacionDestino)
      })));
      
      // IMPORTANTE: Asegurar que los ajustes negativos se mantengan en el estado
      // Verificar que los ajustes negativos estén en la lista antes de establecer el estado
      const ajustesNegativosAntesSet = ajustesOrdenados.filter((a: IFormularioAjuste) => !isEmptyUbic(a.nroUbicacionOrigen) && isEmptyUbic(a.nroUbicacionDestino));
      console.log('🔍 Verificación antes de setAjustes - Ajustes negativos encontrados:', ajustesNegativosAntesSet.length);
      if (ajustesNegativosAntesSet.length > 0) {
        console.log('🔍 Números de formulario de ajustes negativos antes de setAjustes:', ajustesNegativosAntesSet.map((a: IFormularioAjuste) => a.nroFormulario));
      }
      
      // CRÍTICO: Preservar los ajustes que fueron agregados manualmente y que no están en la respuesta del backend
      // Esto evita que se pierdan los ajustes negativos recientemente cargados cuando se carga un nuevo ajuste
      // IMPORTANTE: Usar una función de actualización de estado para acceder al estado anterior
      // IMPORTANTE: Solo preservar cuando NO hay búsqueda activa (searchTerm vacío)
      // Si hay búsqueda, solo mostrar los resultados del backend
      setAjustes((prev) => {
        console.log('🔍🔍🔍 ACTUALIZANDO ESTADO DE AJUSTES');
        console.log('🔍 Estado anterior antes de actualizar:', prev.length, 'ajustes');
        console.log('🔍 Ajustes del backend:', ajustesOrdenados.length, 'ajustes');
        console.log('🔍 SearchTerm actual:', searchTerm);
        console.log('🔍 Números de formulario en estado anterior:', prev.map((a: IFormularioAjuste) => a.nroFormulario));
        
        // Con búsqueda texto o filtro de fechas: solo resultados del backend (sin fusionar estado previo)
        const hayFiltroLista =
          (searchTerm && searchTerm.trim() !== '') ||
          Boolean(fechaDesde) ||
          Boolean(fechaHasta) ||
          resolveNroSucFiltro(filtroNroSuc) != null;
        if (hayFiltroLista) {
          console.log('ℹ️ Filtro de lista activo, usando solo resultados del backend');
          return ajustesOrdenados;
        }
        
        // IMPORTANTE: Si el estado anterior está vacío (0 ajustes), no hay nada que preservar
        // Esto puede suceder cuando se navega desde el formulario y el estado se resetea
        if (prev.length === 0) {
          console.log('ℹ️ Estado anterior está vacío, usando solo los del backend');
          return ajustesOrdenados;
        }
        
        // Verificar ajustes negativos en el estado anterior
        const ajustesNegativosPrev = prev.filter((a: IFormularioAjuste) => !isEmptyUbic(a.nroUbicacionOrigen) && isEmptyUbic(a.nroUbicacionDestino));
        console.log('🔍 Ajustes negativos en estado anterior:', ajustesNegativosPrev.length);
        if (ajustesNegativosPrev.length > 0) {
          console.log('🔍 Números de ajustes negativos en estado anterior:', ajustesNegativosPrev.map((a: IFormularioAjuste) => a.nroFormulario));
        }
        
        // Crear un mapa de los ajustes que vienen del backend para búsqueda rápida
        const ajustesBackendMap = new Map(ajustesOrdenados.map((a: IFormularioAjuste) => [a.nroFormulario, a]));
        
        // Encontrar ajustes en el estado anterior que NO están en la respuesta del backend
        // IMPORTANTE: Preservar TODOS los ajustes que no están en la respuesta del backend
        const ajustesPreservar = prev.filter((a: IFormularioAjuste) => {
          const noEstaEnBackend = !ajustesBackendMap.has(a.nroFormulario);
          if (noEstaEnBackend) {
            console.log('🔍 Ajuste a preservar (no está en backend):', {
              nroFormulario: a.nroFormulario,
              nroUbicacionOrigen: a.nroUbicacionOrigen,
              nroUbicacionDestino: a.nroUbicacionDestino,
              esNegativo: !isEmptyUbic(a.nroUbicacionOrigen) && isEmptyUbic(a.nroUbicacionDestino)
            });
          }
          return noEstaEnBackend;
        });
        
        if (ajustesPreservar.length > 0) {
          console.log('🔍🔍🔍 PRESERVANDO AJUSTES:', ajustesPreservar.length);
          console.log('🔍 Números de formulario preservados:', ajustesPreservar.map((a: IFormularioAjuste) => a.nroFormulario));
          console.log('🔍 Detalles de ajustes preservados:', ajustesPreservar.map((a: IFormularioAjuste) => ({
            nroFormulario: a.nroFormulario,
            nroUbicacionOrigen: a.nroUbicacionOrigen,
            nroUbicacionDestino: a.nroUbicacionDestino,
            esNegativo: !isEmptyUbic(a.nroUbicacionOrigen) && isEmptyUbic(a.nroUbicacionDestino)
          })));
          
          // Combinar los ajustes del backend con los preservados y ordenar
          // IMPORTANTE: Usar los ajustes del backend como base y agregar los preservados
          const ajustesCombinados = [...ajustesOrdenados, ...ajustesPreservar].sort((a, b) => {
            return (b.nroFormulario || 0) - (a.nroFormulario || 0);
          });
          
          console.log('✅✅✅ AJUSTES COMBINADOS (backend + preservados):', ajustesCombinados.length);
          console.log('✅ Primeros 5 números de formulario:', ajustesCombinados.slice(0, 5).map((a: IFormularioAjuste) => a.nroFormulario));
          
          // Verificar ajustes negativos en la lista combinada
          const ajustesNegativosCombinados = ajustesCombinados.filter((a: IFormularioAjuste) => !isEmptyUbic(a.nroUbicacionOrigen) && isEmptyUbic(a.nroUbicacionDestino));
          console.log('✅ Ajustes negativos en lista combinada:', ajustesNegativosCombinados.length);
          if (ajustesNegativosCombinados.length > 0) {
            console.log('✅ Números de ajustes negativos en lista combinada:', ajustesNegativosCombinados.map((a: IFormularioAjuste) => a.nroFormulario));
          }
          
          // VERIFICACIÓN FINAL: Asegurar que todos los ajustes negativos del estado anterior estén en la lista combinada
          const ajustesNegativosPerdidos = ajustesNegativosPrev.filter((a: IFormularioAjuste) => {
            const estaEnCombinados = ajustesCombinados.some((b: IFormularioAjuste) => b.nroFormulario === a.nroFormulario);
            return !estaEnCombinados;
          });
          
          if (ajustesNegativosPerdidos.length > 0) {
            console.error('❌❌❌ ERROR: Ajustes negativos perdidos:', ajustesNegativosPerdidos.map((a: IFormularioAjuste) => a.nroFormulario));
            // Agregar los ajustes negativos perdidos a la lista combinada
            const ajustesCombinadosConPerdidos = [...ajustesCombinados, ...ajustesNegativosPerdidos].sort((a, b) => {
              return (b.nroFormulario || 0) - (a.nroFormulario || 0);
            });
            console.log('✅✅✅ AJUSTES COMBINADOS CON PERDIDOS:', ajustesCombinadosConPerdidos.length);
            return ajustesCombinadosConPerdidos;
          }
          
          return ajustesCombinados;
        }
        
        // Si no hay ajustes para preservar, usar solo los del backend
        console.log('ℹ️ No hay ajustes para preservar, usando solo los del backend');
        console.log('ℹ️ Estado anterior tenía:', prev.length, 'ajustes');
        console.log('ℹ️ Backend retorna:', ajustesOrdenados.length, 'ajustes');
        
        // VERIFICACIÓN: Si había ajustes negativos en el estado anterior pero no están en el backend, preservarlos
        if (ajustesNegativosPrev.length > 0) {
          const _ajustesNegativosEnBackend = ajustesOrdenados.filter((a: IFormularioAjuste) => !isEmptyUbic(a.nroUbicacionOrigen) && isEmptyUbic(a.nroUbicacionDestino));
          const ajustesNegativosFaltantes = ajustesNegativosPrev.filter((a: IFormularioAjuste) => {
            return !ajustesOrdenados.some((b: IFormularioAjuste) => b.nroFormulario === a.nroFormulario);
          });
          
          if (ajustesNegativosFaltantes.length > 0) {
            console.warn('⚠️ Ajustes negativos faltantes en backend, preservándolos:', ajustesNegativosFaltantes.map((a: IFormularioAjuste) => a.nroFormulario));
            const ajustesConNegativosPreservados = [...ajustesOrdenados, ...ajustesNegativosFaltantes].sort((a, b) => {
              return (b.nroFormulario || 0) - (a.nroFormulario || 0);
            });
            console.log('✅✅✅ AJUSTES CON NEGATIVOS PRESERVADOS:', ajustesConNegativosPreservados.length);
            return ajustesConNegativosPreservados;
          }
        }
        
        return ajustesOrdenados;
      });
      setTotalCount(response.totalCount);
      
      const ajustesNegativos = ajustesOrdenados.filter((a: IFormularioAjuste) => !isEmptyUbic(a.nroUbicacionOrigen) && isEmptyUbic(a.nroUbicacionDestino));
      console.log('📊 Total de ajustes cargados del backend:', ajustesOrdenados.length);
      console.log('📊 Ajustes negativos detectados en carga inicial:', ajustesNegativos.length);
      
      if (ajustesNegativos.length > 0) {
        console.log('📊 Números de formulario de ajustes negativos detectados:', ajustesNegativos.map((a: IFormularioAjuste) => a.nroFormulario));
        console.log('📊 Detalles de ajustes negativos detectados:', ajustesNegativos.map((a: IFormularioAjuste) => ({
          nroFormulario: a.nroFormulario,
          nroUbicacionOrigen: a.nroUbicacionOrigen,
          nroUbicacionDestino: a.nroUbicacionDestino,
          tipoCliente: a.tipoCliente,
          nroTipoFormulario: a.nroTipoFormulario
        })));
      } else {
        console.warn('⚠️ NO se detectaron ajustes negativos en la carga inicial del backend');
        console.warn('⚠️ Esto puede significar que los ajustes negativos no están en esta página del servidor o no se están guardando correctamente');
      }
      
      // Verificar si hay un ajuste guardado recientemente que debería estar en la lista
      const nroFormularioGuardado = (location.state as any)?.nroFormularioGuardado;
      if (nroFormularioGuardado) {
        const ajusteEncontrado = ajustesOrdenados.find((a: IFormularioAjuste) => a.nroFormulario === nroFormularioGuardado);
        if (ajusteEncontrado) {
          console.log('✅ Ajuste guardado encontrado en la lista:', ajusteEncontrado.nroFormulario);
          console.log('✅ Detalles del ajuste encontrado:', {
            nroFormulario: ajusteEncontrado.nroFormulario,
            nroUbicacionOrigen: ajusteEncontrado.nroUbicacionOrigen,
            nroUbicacionDestino: ajusteEncontrado.nroUbicacionDestino,
            esNegativo: !isEmptyUbic(ajusteEncontrado.nroUbicacionOrigen) && isEmptyUbic(ajusteEncontrado.nroUbicacionDestino)
          });
        } else {
          console.warn('⚠️ Ajuste guardado NO encontrado en la primera página. Nro. Formulario:', nroFormularioGuardado);
          console.warn('⚠️ Esto puede significar que el ajuste está en otra página del servidor.');
          console.log('📋 Números de formulario en esta página:', ajustesOrdenados.map((a: IFormularioAjuste) => a.nroFormulario));
          
          // CRÍTICO: Buscar el ajuste inmediatamente y agregarlo a la lista
          // Esto asegura que los ajustes nuevos aparezcan incluso si no están en la primera página del servidor
          // IMPORTANTE: No usar setTimeout, buscar inmediatamente para que el ajuste aparezca de inmediato
          try {
            console.log('🔍 Buscando ajuste específico por número de formulario (INMEDIATAMENTE):', nroFormularioGuardado);
            const ajusteCompleto = await ajusteInventarioService.getAjuste(nroFormularioGuardado);
            if (ajusteCompleto) {
              console.log('✅ Ajuste encontrado (raw):', ajusteCompleto);
              // Extraer el formulario del ajuste completo
              const ajusteFormulario = ajusteCompleto.formulario || ajusteCompleto;
              console.log('✅ Formulario del ajuste (raw):', ajusteFormulario);
              
              // Normalizar las ubicaciones del ajuste encontrado
              const nroUbicacionOrigenNormalizado = (ajusteFormulario.nroUbicacionOrigen === 0 || ajusteFormulario.nroUbicacionOrigen === null || ajusteFormulario.nroUbicacionOrigen === undefined) ? null : ajusteFormulario.nroUbicacionOrigen;
              const nroUbicacionDestinoNormalizado = (ajusteFormulario.nroUbicacionDestino === 0 || ajusteFormulario.nroUbicacionDestino === null || ajusteFormulario.nroUbicacionDestino === undefined) ? null : ajusteFormulario.nroUbicacionDestino;
              
              // Verificar que el ajuste tenga las ubicaciones correctas después de normalizar
              const tieneOrigen = !isEmptyUbic(nroUbicacionOrigenNormalizado);
              const tieneDestino = !isEmptyUbic(nroUbicacionDestinoNormalizado);
              const esNegativo = tieneOrigen && !tieneDestino;
              
              console.log('✅ Verificación del ajuste encontrado (después de normalización):', {
                tieneOrigen,
                tieneDestino,
                esNegativo,
                nroUbicacionOrigenOriginal: ajusteFormulario.nroUbicacionOrigen,
                nroUbicacionDestinoOriginal: ajusteFormulario.nroUbicacionDestino,
                nroUbicacionOrigenNormalizado,
                nroUbicacionDestinoNormalizado
              });
              
              // Agregar el ajuste al inicio de la lista si no está
              setAjustes((prev) => {
                const existe = prev.find((a: IFormularioAjuste) => a.nroFormulario === nroFormularioGuardado);
                if (!existe) {
                  // Normalizar las ubicaciones del nuevo ajuste
                  const nuevoAjuste = {
                    ...ajusteFormulario,
                    nroUbicacionOrigen: nroUbicacionOrigenNormalizado,
                    nroUbicacionDestino: nroUbicacionDestinoNormalizado
                  } as IFormularioAjuste;
                  
                  console.log('✅✅✅ AGREGANDO NUEVO AJUSTE A LA LISTA:', {
                    nroFormulario: nuevoAjuste.nroFormulario,
                    nroUbicacionOrigen: nuevoAjuste.nroUbicacionOrigen,
                    nroUbicacionDestino: nuevoAjuste.nroUbicacionDestino,
                    esNegativo: !isEmptyUbic(nuevoAjuste.nroUbicacionOrigen) && isEmptyUbic(nuevoAjuste.nroUbicacionDestino),
                    tipoAjuste: esNegativo ? 'negativo' : (tieneOrigen && tieneDestino ? 'transferencia' : 'positivo')
                  });
                  
                  // IMPORTANTE: Agregar el nuevo ajuste y preservar todos los ajustes existentes
                  // Ordenar para mantener el orden descendente
                  const nuevosAjustes = [nuevoAjuste, ...prev].sort((a, b) => {
                    return (b.nroFormulario || 0) - (a.nroFormulario || 0);
                  });
                  
                  console.log('✅✅✅ AJUSTE AGREGADO. Total de ajustes:', nuevosAjustes.length);
                  console.log('✅✅✅ Primeros 5 números de formulario:', nuevosAjustes.slice(0, 5).map((a: IFormularioAjuste) => a.nroFormulario));
                  
                  // Verificar ajustes negativos en la lista actualizada
                  const ajustesNegativosActualizados = nuevosAjustes.filter((a: IFormularioAjuste) => !isEmptyUbic(a.nroUbicacionOrigen) && isEmptyUbic(a.nroUbicacionDestino));
                  console.log('✅ Ajustes negativos en lista actualizada:', ajustesNegativosActualizados.length);
                  if (ajustesNegativosActualizados.length > 0) {
                    console.log('✅ Números de ajustes negativos en lista actualizada:', ajustesNegativosActualizados.map((a: IFormularioAjuste) => a.nroFormulario));
                  }
                  
                  return nuevosAjustes;
                }
                console.log('ℹ️ Ajuste ya existe en la lista');
                return prev;
              });
            } else {
              console.error('❌ El ajuste no se encontró en el backend. Nro. Formulario:', nroFormularioGuardado);
            }
          } catch (error) {
            console.error('❌ Error al buscar ajuste específico:', error);
            console.error('❌ Detalles del error:', error);
          }
        }
      }
    } catch (error) {
      console.error('Error al cargar ajustes:', error);
      enqueueSnackbar('Error al cargar los ajustes', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleFechaDesdeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFechaDesde(event.target.value);
    setPage(0);
  };

  const handleFechaHastaChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFechaHasta(event.target.value);
    setPage(0);
  };

  const handleImprimirPDF = async (nroFormulario: number) => {
    try {
      const token = getAccessToken();
      if (!token) {
        enqueueSnackbar('No hay token de autenticación', { variant: 'error' });
        return;
      }

      const response = await api.get(`/api/ajustes/${nroFormulario}/pdf`, {
        responseType: 'blob',
      });

      const cd = (response.headers as any)?.['content-disposition'] as string | undefined;
      setPdfFileName(getFileNameFromContentDisposition(cd));

      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      setPdfUrl((prev) => {
        if (prev) window.URL.revokeObjectURL(prev);
        return blobUrl;
      });
      setSelectedAjusteNro(nroFormulario);
    } catch (error) {
      console.error('Error al generar el reporte:', error);
      enqueueSnackbar('Error al generar el reporte PDF', { variant: 'error' });
    }
  };

  const handleClosePDF = () => {
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
    }
    setPdfUrl(null);
    setSelectedAjusteNro(null);
    setPdfFileName(null);
  };

  const handleExportarExcel = async (nroFormulario: number) => {
    try {
      setExportingExcelNro(nroFormulario);
      const data = await ajusteInventarioService.getAjuste(nroFormulario);
      const raw = (data.formulario || data) as IFormularioAjuste & { items?: IItemAjuste[] };
      const formulario: IFormularioAjuste = raw;
      const items: IItemAjuste[] = data.items || raw.items || [];
      exportAjusteInventarioExcel(formulario, items);
      enqueueSnackbar('Excel exportado correctamente', { variant: 'success' });
    } catch (error) {
      console.error('Error al exportar Excel:', error);
      const message = error instanceof Error && error.message === 'No hay ítems para exportar'
        ? error.message
        : 'Error al exportar a Excel';
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setExportingExcelNro(null);
    }
  };

  const abrirEtiquetas = async (nroFormulario: number, tipo: string) => {
    try {
      const data = await ajusteInventarioService.getAjuste(nroFormulario);
      const detalles = detallesEtiquetaDesdeAjuste(data);
      if (detalles.length === 0) {
        enqueueSnackbar('Este movimiento no tiene productos para etiquetar.', { variant: 'info' });
        return;
      }
      setEtiquetasCtx({
        nroFormulario,
        detalles,
        textoOrigen: textoOrigenMovimiento(tipo),
      });
      setEtiquetasOpen(true);
    } catch (error) {
      console.error('Error al cargar productos para etiquetas:', error);
      enqueueSnackbar('No se pudieron cargar los productos para imprimir etiquetas.', { variant: 'error' });
    }
  };

  useEffect(() => {
    const st = location.state as {
      pedirEtiquetas?: boolean;
      nroFormularioGuardado?: number;
      tipoAjusteGuardado?: string;
    } | undefined;
    if (!st?.pedirEtiquetas || !st.nroFormularioGuardado || etiquetasPedidasRef.current) return;
    etiquetasPedidasRef.current = true;
    let cancelled = false;
    const nro = st.nroFormularioGuardado;
    const tipo = st.tipoAjusteGuardado || '';
    navigate(location.pathname, {
      replace: true,
      state: {
        nroFormularioGuardado: nro,
        tipoAjusteGuardado: st.tipoAjusteGuardado,
      },
    });
    void (async () => {
      try {
        const data = await ajusteInventarioService.getAjuste(nro);
        if (cancelled) return;
        const detalles = detallesEtiquetaDesdeAjuste(data);
        if (detalles.length === 0) {
          enqueueSnackbar('Este movimiento no tiene productos para etiquetar.', { variant: 'info' });
          return;
        }
        setEtiquetasCtx({
          nroFormulario: nro,
          detalles,
          textoOrigen: textoOrigenMovimiento(tipo),
        });
        setEtiquetasOpen(true);
      } catch (error) {
        if (cancelled) return;
        console.error('Error al cargar productos para etiquetas:', error);
        enqueueSnackbar('No se pudieron cargar los productos para imprimir etiquetas.', { variant: 'error' });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Log para verificar el estado de ajustes antes del filtrado
  useEffect(() => {
    if (ajustes.length > 0) {
      const ajustesNegativosEnEstado = ajustes.filter((a: IFormularioAjuste) => !isEmptyUbic(a.nroUbicacionOrigen) && isEmptyUbic(a.nroUbicacionDestino));
      console.log('🔍 Estado de ajustes antes de filtrado:', {
        totalAjustes: ajustes.length,
        ajustesNegativosEnEstado: ajustesNegativosEnEstado.length,
        numerosNegativos: ajustesNegativosEnEstado.map((a: IFormularioAjuste) => a.nroFormulario)
      });
    }
  }, [ajustes]);
  
  const ajustesFiltrados = ajustes.filter((ajuste) => {
    const tieneOrigen = !isEmptyUbic(ajuste.nroUbicacionOrigen);
    const tieneDestino = !isEmptyUbic(ajuste.nroUbicacionDestino);
    const esNegativo = tieneOrigen && !tieneDestino;
    
    let pasaFiltro = true;
    if (tipoMovimiento === 'todos') {
      pasaFiltro = true;
    } else if (tipoMovimiento === 'positivo') {
      pasaFiltro = !tieneOrigen && tieneDestino;
    } else if (tipoMovimiento === 'negativo') {
      pasaFiltro = esNegativo;
    } else if (tipoMovimiento === 'transferencia') {
      pasaFiltro = tieneOrigen && tieneDestino;
    }
    
    // Log para depuración solo para ajustes negativos o primeros 3
    if (esNegativo || ajustes.indexOf(ajuste) < 3) {
      console.log(`🔍 Filtrado ajuste ${ajuste.nroFormulario}:`, {
        nroUbicacionOrigen: ajuste.nroUbicacionOrigen,
        nroUbicacionDestino: ajuste.nroUbicacionDestino,
        tieneOrigen,
        tieneDestino,
        esNegativo,
        tipoMovimiento,
        pasaFiltro
      });
    }
    
    return pasaFiltro;
  });
  
  // Log detallado de ajustes negativos filtrados
  const ajustesNegativosFiltrados = ajustesFiltrados.filter((ajuste) => {
    const tieneOrigen = !isEmptyUbic(ajuste.nroUbicacionOrigen);
    const tieneDestino = !isEmptyUbic(ajuste.nroUbicacionDestino);
    return tieneOrigen && !tieneDestino;
  });
  
  console.log(`📊 Ajustes filtrados (tipo: ${tipoMovimiento}):`, ajustesFiltrados.length, 'de', ajustes.length);
  console.log(`📊 Ajustes negativos en lista filtrada:`, ajustesNegativosFiltrados.length);
  if (ajustesNegativosFiltrados.length > 0) {
    console.log(`📊 Números de ajustes negativos mostrados:`, ajustesNegativosFiltrados.map((a: IFormularioAjuste) => a.nroFormulario));
  }

  const getTipoAjuste = (ajuste: IFormularioAjuste): string => {
    const tieneOrigen = !isEmptyUbic(ajuste.nroUbicacionOrigen);
    const tieneDestino = !isEmptyUbic(ajuste.nroUbicacionDestino);
    if (!tieneOrigen && tieneDestino) return 'Ajuste Positivo';
    if (tieneOrigen && !tieneDestino) return 'Ajuste Negativo';
    if (tieneOrigen && tieneDestino) return 'Transferencia';
    return 'Desconocido';
  };

  const _getColorAjuste = (ajuste: IFormularioAjuste): string => {
    const tieneOrigen = !isEmptyUbic(ajuste.nroUbicacionOrigen);
    const tieneDestino = !isEmptyUbic(ajuste.nroUbicacionDestino);
    if (!tieneOrigen && tieneDestino) return theme.palette.primary.main; // positivo (naranja)
    if (tieneOrigen && !tieneDestino) return theme.palette.error.main;   // negativo (rojo)
    if (tieneOrigen && tieneDestino) return '#1976d2'; // transferencia (azul)
    return 'inherit';
  };

  const formatComprobante = (ajuste: IFormularioAjuste) => {
    const { letra, nroSuc, nroComprobante } = ajuste;
    if (!letra || nroSuc === undefined || nroComprobante === undefined || nroSuc === null || nroComprobante === null) {
      return '-';
    }

    const formatPart = (value: number | string, length: number) => {
      if (value === null || value === undefined) {
        return ''.padStart(length, '0');
      }
      const numeric = Number(value);
      if (!Number.isNaN(numeric)) {
        return numeric.toString().padStart(length, '0');
      }
      return String(value).padStart(length, '0');
    };

    const sucursal = formatPart(nroSuc, 5);
    const comprobante = formatPart(nroComprobante, 8);
    const letraNormalizada = letra.trim().toUpperCase();

    if (!letraNormalizada) {
      return '-';
    }

    return `${letraNormalizada}-${sucursal}-${comprobante}`;
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card 
        sx={{ 
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {/* Header con controles */}
          <Stack 
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between" 
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600,
                color: '#1976d2'
              }}
            >
              Transferencias y Ajustes
            </Typography>
            <Stack 
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              <Can permiso={PERMISO_AJUSTE_INVENTARIO.positivo}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/inventario/transferencias/nuevo', { state: { tipo: 'positivo' } })}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1,
                    textTransform: 'none',
                    fontWeight: 500,
                    boxShadow: '0 2px 4px rgba(234, 88, 12, 0.35)',
                    '&:hover': {
                      boxShadow: '0 4px 8px rgba(234, 88, 12, 0.45)',
                    }
                  }}
                >
                  {isMobile ? 'Ajuste +' : 'Ajuste Positivo'}
                </Button>
              </Can>
              <Can permiso={PERMISO_AJUSTE_INVENTARIO.negativo}>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<RemoveIcon />}
                  onClick={() => navigate('/inventario/transferencias/nuevo', { state: { tipo: 'negativo' } })}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1,
                    textTransform: 'none',
                    fontWeight: 500,
                    boxShadow: '0 2px 4px rgba(244, 67, 54, 0.3)',
                    '&:hover': {
                      boxShadow: '0 4px 8px rgba(244, 67, 54, 0.4)',
                    }
                  }}
                >
                  {isMobile ? 'Ajuste -' : 'Ajuste Negativo'}
                </Button>
              </Can>
              <Can permiso={PERMISO_AJUSTE_INVENTARIO.transferencia}>
                <Button
                  variant="contained"
                  color="inherit"
                  startIcon={<SwapHorizIcon />}
                  onClick={() => navigate('/inventario/transferencias/nuevo', { state: { tipo: 'transferencia' } })}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1,
                    textTransform: 'none',
                    fontWeight: 500,
                    backgroundColor: '#1976d2',
                    color: '#fff',
                    boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                    '&:hover': {
                      backgroundColor: '#1565c0',
                      boxShadow: '0 4px 8px rgba(21, 101, 192, 0.4)',
                    }
                  }}
                >
                  Transferencia
                </Button>
              </Can>
            </Stack>
          </Stack>

          {/* Controles de búsqueda y filtro */}
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            flexWrap: 'wrap',
            gap: 2,
            mb: 3,
            alignItems: { lg: 'flex-start' },
          }}>
            <TextField
              label="Buscar"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={handleSearchChange}
              sx={{
                flex: { lg: '1 1 200px' },
                minWidth: { xs: '100%', lg: 200 },
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: '#fafafa',
                  '&:hover': {
                    bgcolor: '#f5f5f5',
                  },
                  '&.Mui-focused': {
                    bgcolor: '#ffffff',
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#666' }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Fecha desde"
              type="date"
              variant="outlined"
              size="small"
              value={fechaDesde}
              onChange={handleFechaDesdeChange}
              InputLabelProps={{ shrink: true }}
              sx={{
                minWidth: { xs: '100%', sm: 160 },
                width: { xs: '100%', sm: 'auto' },
                flexShrink: 0,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: '#fafafa',
                  '&:hover': { bgcolor: '#f5f5f5' },
                  '&.Mui-focused': { bgcolor: '#ffffff' },
                },
              }}
            />
            <TextField
              label="Fecha hasta"
              type="date"
              variant="outlined"
              size="small"
              value={fechaHasta}
              onChange={handleFechaHastaChange}
              InputLabelProps={{ shrink: true }}
              sx={{
                minWidth: { xs: '100%', sm: 160 },
                width: { xs: '100%', sm: 'auto' },
                flexShrink: 0,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: '#fafafa',
                  '&:hover': { bgcolor: '#f5f5f5' },
                  '&.Mui-focused': { bgcolor: '#ffffff' },
                },
              }}
            />
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              flexShrink: 0,
              width: { xs: '100%', sm: 'auto' },
            }}>
              <SearchableSelect
                label="Filtrar por Tipo"
                size="small"
                options={[...TIPO_MOVIMIENTO_AJUSTE_OPTIONS]}
                value={tipoMovimiento}
                onChange={(value) => setTipoMovimiento(value ?? 'todos')}
                sx={{
                  minWidth: { xs: '100%', sm: 220 },
                  width: { xs: '100%', sm: 220 },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': { bgcolor: '#f5f5f5' },
                    '&.Mui-focused': { bgcolor: '#ffffff' },
                  },
                }}
              />
              <FiltroSucursalVentaSelect
                value={filtroNroSuc}
                onChange={(v) => { setFiltroNroSuc(v); setPage(0); }}
                loading={loadingSucursales}
                sucursales={sucursales}
                size="small"
                sx={{
                  minWidth: { xs: '100%', sm: 220 },
                  width: { xs: '100%', sm: 220 },
                }}
              />
            </Box>
          </Box>

          {/* Tabla o Cards según el tamaño de pantalla */}
          {isMobile ? (
            <Stack spacing={2}>
              {ajustesFiltrados.map((ajuste) => {
                const tipo = getTipoAjuste(ajuste);
                const tieneOrigen = !isEmptyUbic(ajuste.nroUbicacionOrigen);
                const tieneDestino = !isEmptyUbic(ajuste.nroUbicacionDestino);
                const esNegativo = tieneOrigen && !tieneDestino;
                const esPositivo = !tieneOrigen && tieneDestino;
                const _esTransferencia = tieneOrigen && tieneDestino;
                
                const chipColor = esNegativo ? '#f44336' : esPositivo ? '#1976d2' : '#2196f3';
                const chipBgColor = esNegativo ? '#ffebee' : esPositivo ? '#bbdefb' : '#e3f2fd';
                
                return (
                  <Card 
                    key={ajuste.nroFormulario}
                    sx={{
                      borderRadius: 2,
                      border: '1px solid #e0e0e0',
                      '&:hover': {
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    <CardContent>
                      <Stack spacing={1.5}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Chip
                            label={tipo}
                            sx={{
                              bgcolor: chipBgColor,
                              color: chipColor,
                              fontWeight: 600,
                              borderRadius: 1.5,
                            }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            Nro: {ajuste.nroFormulario}
                          </Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Fecha:</strong> {new Date(ajuste.fechaEmision!).toLocaleDateString()}
                        </Typography>
                        {formatComprobante(ajuste) !== '-' && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Comprobante:</strong> {formatComprobante(ajuste)}
                          </Typography>
                        )}
                        {ajuste.descripcion && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Descripción:</strong> {ajuste.descripcion}
                          </Typography>
                        )}
                        {ajuste.detUbicacionOrigen && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Ubicación Origen:</strong> {ajuste.detUbicacionOrigen}
                          </Typography>
                        )}
                        {ajuste.detUbicacionDestino && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Ubicación Destino:</strong> {ajuste.detUbicacionDestino}
                          </Typography>
                        )}
                        {ajuste.usuario && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Usuario:</strong> {ajuste.usuario}
                          </Typography>
                        )}
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                          <Tooltip title="Imprimir PDF">
                            <IconButton
                              onClick={() => handleImprimirPDF(ajuste.nroFormulario)}
                              disabled={loading}
                              size="small"
                              sx={{
                                bgcolor: '#1976d2',
                                color: '#fff',
                                borderRadius: 1.5,
                                '&:hover': { bgcolor: '#1565c0' },
                                '&.Mui-disabled': {
                                  bgcolor: '#e0e0e0',
                                  color: '#9e9e9e'
                                }
                              }}
                            >
                              <PrintIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Exportar Excel">
                            <IconButton
                              onClick={() => void handleExportarExcel(ajuste.nroFormulario)}
                              disabled={loading || exportingExcelNro === ajuste.nroFormulario}
                              size="small"
                              sx={{
                                bgcolor: '#2e7d32',
                                color: '#fff',
                                borderRadius: 1.5,
                                '&:hover': { bgcolor: '#1b5e20' },
                                '&.Mui-disabled': {
                                  bgcolor: '#e0e0e0',
                                  color: '#9e9e9e'
                                }
                              }}
                            >
                              <FileDownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Imprimir etiquetas">
                            <IconButton
                              onClick={() => void abrirEtiquetas(ajuste.nroFormulario, tipo)}
                              disabled={loading}
                              size="small"
                              sx={{
                                bgcolor: '#00897b',
                                color: '#fff',
                                borderRadius: 1.5,
                                '&:hover': { bgcolor: '#00695c' },
                                '&.Mui-disabled': {
                                  bgcolor: '#e0e0e0',
                                  color: '#9e9e9e'
                                }
                              }}
                            >
                              <BarcodeIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
              {ajustesFiltrados.length === 0 && (
                <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                  No hay ajustes para mostrar
                </Typography>
              )}
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas"
                labelDisplayedRows={({ from, to, count }) => 
                  `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
                }
              />
            </Stack>
          ) : (
            <TableContainer 
              component={Paper}
              sx={{
                borderRadius: 2,
                boxShadow: 'none',
                border: '1px solid #e0e0e0',
              }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Nro. Formulario</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Tipo</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                    <TableCell sx={{ fontWeight: 600, display: { xs: 'none', lg: 'table-cell' }, minWidth: 110 }}>Comprobante</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Ubicación Origen</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Ubicación Destino</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Usuario</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ajustesFiltrados.map((ajuste) => {
                    const tipo = getTipoAjuste(ajuste);
                    const tieneOrigen = !isEmptyUbic(ajuste.nroUbicacionOrigen);
                    const tieneDestino = !isEmptyUbic(ajuste.nroUbicacionDestino);
                    const esNegativo = tieneOrigen && !tieneDestino;
                    const esPositivo = !tieneOrigen && tieneDestino;
                    const _esTransferencia = tieneOrigen && tieneDestino;
                    
                    const chipColor = esNegativo ? '#f44336' : esPositivo ? '#1976d2' : '#2196f3';
                    const chipBgColor = esNegativo ? '#ffebee' : esPositivo ? '#bbdefb' : '#e3f2fd';

                    return (
                      <TableRow 
                        key={ajuste.nroFormulario}
                        sx={{
                          '&:hover': {
                            bgcolor: '#f9f9f9',
                          },
                        }}
                      >
                        <TableCell sx={{ fontWeight: 500 }}>{ajuste.nroFormulario}</TableCell>
                        <TableCell>
                          <Chip
                            label={tipo}
                            size="small"
                            sx={{
                              bgcolor: chipBgColor,
                              color: chipColor,
                              fontWeight: 600,
                              borderRadius: 1.5,
                            }}
                          />
                        </TableCell>
                        <TableCell>{new Date(ajuste.fechaEmision!).toLocaleDateString()}</TableCell>
                        <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                          {formatComprobante(ajuste)}
                        </TableCell>
                        <TableCell>{ajuste.descripcion}</TableCell>
                        <TableCell>{ajuste.detUbicacionOrigen || '-'}</TableCell>
                        <TableCell>{ajuste.detUbicacionDestino || '-'}</TableCell>
                        <TableCell>{ajuste.usuario}</TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            <Tooltip title="Imprimir PDF">
                              <IconButton
                                onClick={() => handleImprimirPDF(ajuste.nroFormulario)}
                                disabled={loading}
                                size="small"
                                sx={{
                                  bgcolor: '#1976d2',
                                  color: '#fff',
                                  borderRadius: 1.5,
                                  width: 32,
                                  height: 32,
                                  '&:hover': { 
                                    bgcolor: '#1565c0',
                                    transform: 'scale(1.1)',
                                  },
                                  transition: 'all 0.2s',
                                  '&.Mui-disabled': {
                                    bgcolor: '#e0e0e0',
                                    color: '#9e9e9e'
                                  }
                                }}
                              >
                                <PrintIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Exportar Excel">
                              <IconButton
                                onClick={() => void handleExportarExcel(ajuste.nroFormulario)}
                                disabled={loading || exportingExcelNro === ajuste.nroFormulario}
                                size="small"
                                sx={{
                                  bgcolor: '#2e7d32',
                                  color: '#fff',
                                  borderRadius: 1.5,
                                  width: 32,
                                  height: 32,
                                  '&:hover': {
                                    bgcolor: '#1b5e20',
                                    transform: 'scale(1.1)',
                                  },
                                  transition: 'all 0.2s',
                                  '&.Mui-disabled': {
                                    bgcolor: '#e0e0e0',
                                    color: '#9e9e9e'
                                  }
                                }}
                              >
                                <FileDownloadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Imprimir etiquetas">
                              <IconButton
                                onClick={() => void abrirEtiquetas(ajuste.nroFormulario, tipo)}
                                disabled={loading}
                                size="small"
                                sx={{
                                  bgcolor: '#00897b',
                                  color: '#fff',
                                  borderRadius: 1.5,
                                  width: 32,
                                  height: 32,
                                  '&:hover': {
                                    bgcolor: '#00695c',
                                    transform: 'scale(1.1)',
                                  },
                                  transition: 'all 0.2s',
                                  '&.Mui-disabled': {
                                    bgcolor: '#e0e0e0',
                                    color: '#9e9e9e'
                                  }
                                }}
                              >
                                <BarcodeIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
                labelDisplayedRows={({ from, to, count }) => 
                  `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
                }
                sx={{
                  borderTop: '1px solid #e0e0e0',
                }}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {pdfUrl && selectedAjusteNro != null && (
        <PDFViewer
          pdfUrl={pdfUrl}
          onClose={handleClosePDF}
          title={`Ajuste / transferencia ${selectedAjusteNro}`}
          documentId={`${selectedAjusteNro}`}
          documentType="ajuste"
          downloadFileName={pdfFileName ?? undefined}
        />
      )}

      {etiquetasCtx ? (
        <ImprimirEtiquetasCodigosDialog
          open={etiquetasOpen}
          nroFormulario={etiquetasCtx.nroFormulario}
          detalles={etiquetasCtx.detalles}
          textoOrigen={etiquetasCtx.textoOrigen}
          fileName={`etiquetas-${etiquetasCtx.nroFormulario}.pdf`}
          obtenerPdf={(id, items) => ajusteInventarioService.getEtiquetasCodigosPdf(id, items)}
          onClose={() => setEtiquetasOpen(false)}
          onPdfReady={(url, fileName) => {
            setPdfEtiquetas((prev) => {
              if (prev?.url) URL.revokeObjectURL(prev.url);
              return { url, fileName };
            });
          }}
        />
      ) : null}

      {pdfEtiquetas && etiquetasCtx ? (
        <PDFViewer
          pdfUrl={pdfEtiquetas.url}
          title={`Etiquetas ${etiquetasCtx.nroFormulario}`}
          documentId={String(etiquetasCtx.nroFormulario)}
          documentType="informeCliente"
          downloadFileName={pdfEtiquetas.fileName}
          onClose={() => {
            URL.revokeObjectURL(pdfEtiquetas.url);
            setPdfEtiquetas(null);
          }}
        />
      ) : null}
    </Box>
  );
};

export default Transferencias; 